/**
 * Bilder krymps i webbläsaren innan de laddas upp. Ett mobilfoto på 3–7 MB
 * blir då några hundra kB, utan att synas i butiken (den visar ändå högst
 * 1600 px breda bilder). Går något fel laddas originalet upp som förut.
 */

/** Längsta sidan efter krympning. */
export const MAX_IMAGE_EDGE = 2000;
/** Mindre bilder än så här, som redan får plats, lämnas orörda. */
export const SMALL_IMAGE_BYTES = 400 * 1024;
const JPEG_QUALITY = 0.85;

/** Största video som går att ladda upp. Videor kan inte krympas i webbläsaren. */
export const MAX_VIDEO_BYTES = 15 * 1024 * 1024;

/** Format som går att rita om. GIF (rörlig) och SVG (vektor) lämnas orörda. */
const COMPRESSIBLE = ['image/jpeg', 'image/png', 'image/webp'];

export const canCompress = (type: string): boolean => COMPRESSIBLE.includes(type.toLowerCase());

/** Ny storlek med samma proportioner, där längsta sidan är högst `max`. */
export const fitWithin = (width: number, height: number, max: number = MAX_IMAGE_EDGE): { width: number; height: number } => {
    const longest = Math.max(width, height);
    if (longest <= max) return { width, height };
    const scale = max / longest;
    return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
};

/** Filnamnet med ändelse för det nya formatet: "bild.PNG" → "bild.jpg". */
export const renameFor = (name: string, type: string): string => {
    const ext = type === 'image/jpeg' ? 'jpg' : type === 'image/webp' ? 'webp' : 'png';
    const base = name.replace(/\.[^./]+$/, '') || 'bild';
    return `${base}.${ext}`;
};

/** Videon är för stor: meddelandet att visa, annars null. */
export const videoTooLarge = (file: { type: string; size: number }): string | null => {
    if (!file.type.startsWith('video/') || file.size <= MAX_VIDEO_BYTES) return null;
    const mb = (file.size / 1024 / 1024).toFixed(0);
    return `Videon är ${mb} MB. Högst ${MAX_VIDEO_BYTES / 1024 / 1024} MB går att ladda upp, eftersom den laddas ner av varje besökare. Korta den eller spara den i lägre kvalitet.`;
};

const hasTransparency = (ctx: CanvasRenderingContext2D, width: number, height: number): boolean => {
    const data = ctx.getImageData(0, 0, width, height).data;
    for (let i = 3; i < data.length; i += 4) if (data[i] < 255) return true;
    return false;
};

const toBlob = (canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> =>
    new Promise(resolve => canvas.toBlob(resolve, type, quality));

/**
 * Krympt kopia av bilden, eller originalet om den redan är liten, inte går
 * att rita om, eller inte blev mindre. Foton blir JPEG; bilder med
 * genomskinlighet (t.ex. loggor) behåller den, som WebP eller PNG.
 */
export const compressImage = async (file: File): Promise<File> => {
    if (!canCompress(file.type) || typeof document === 'undefined' || typeof createImageBitmap !== 'function') return file;
    try {
        const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
        const size = fitWithin(bitmap.width, bitmap.height);
        const resized = size.width !== bitmap.width || size.height !== bitmap.height;
        if (!resized && file.size <= SMALL_IMAGE_BYTES) {
            bitmap.close();
            return file;
        }

        const canvas = document.createElement('canvas');
        canvas.width = size.width;
        canvas.height = size.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) { bitmap.close(); return file; }
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(bitmap, 0, 0, size.width, size.height);
        bitmap.close();

        let blob: Blob | null;
        if (file.type !== 'image/jpeg' && hasTransparency(ctx, size.width, size.height)) {
            // Safari kan inte spara WebP och ger då PNG; båda behåller genomskinligheten.
            blob = await toBlob(canvas, 'image/webp', JPEG_QUALITY);
            if (!blob || blob.type !== 'image/webp') blob = await toBlob(canvas, 'image/png');
        } else {
            blob = await toBlob(canvas, 'image/jpeg', JPEG_QUALITY);
        }

        if (!blob || blob.size >= file.size) return file;
        return new File([blob], renameFor(file.name, blob.type), { type: blob.type, lastModified: Date.now() });
    } catch {
        return file;
    }
};
